import MomentBD from 'moment-business-days'
import { DateTime } from 'luxon'
import { logHeardError } from './errorHelpers'

/**
 * @deprecated Use DATE_FORMATS_LUXON instead. This enum will be removed in PR 6.
 * Only kept for backward compatibility while feature-layer files (PRs 3–5) migrate.
 */
export enum DATE_FORMATS {
  // For date inputs.  Generally should be used where user is entering values
  INPUT = 'MM-DD-YYYY',
  // Standard short display.  If you're displaying a date value to the user it will probably be this one or the long version
  DISPLAY_SHORT = 'MMM DD, YYYY',
  // Standard long date display
  DISPLAY_LONG = 'MMMM DD, YYYY',
  // Standard full date/time display
  DISPLAY_FULL = 'MMM DD YYYY h:mm:ss a',
  // Numbers only display
  DISPLAY_SIMPLE = 'MM/DD/YYYY',
  // Used for gusto api calls. Gets and posts need to use this
  GUSTO_SUBMIT = 'YYYY-MM-DD',
  // Used when we need to know month and year only ie monthly reports
  MONTH_YEAR = 'MMM YYYY',
  // Same as MONTH_YEAR but hyphen-separated (e.g. 'Jan-2024') — used for compound keys
  MONTH_YEAR_DASH = 'MMM-YYYY',
  // Abbreviated month name only (e.g. 'Jan')
  MONTH_ABBREV = 'MMM',
  // Full month name only (e.g. 'January')
  MONTH_LONG = 'MMMM',
  // Numeric month and day (e.g. '01-15')
  MONTH_DAY_NUMERIC = 'MM-DD',
  // Used when year isn't needed
  MONTH_DAY = 'MMM D',
  // Used for quarter displays
  QUARTER = 'Q, YYYY',
  // Quarter number only, e.g. '1'
  QUARTER_NUM = 'Q',
  // Quarter prefix followed by year, e.g. 'Q1 2024'
  QUARTER_PREFIX_YEAR = '[Q]Q YYYY',
  // Used for time display.  Can be combined with above
  TIME = 'h:mm A',
  // Used where day of the week should be displayed, ie in Payroll reminders
  DAY_OF_WEEK_DAY_MONTH = 'dddd MMMM DD',
  // Used for some forms
  TIMESTAMP = 'YYYY-MM-DD HH:mm:ss',
  DISPLAY_TIMEZONE = 'MMM DD YYYY h:mm a z',
  // https://datatracker.ietf.org/doc/html/rfc5545 used by some apis
  RFC5545 = 'YYYYMMDDTHHmmss[Z]',
  UTC = 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]',
  YEAR = 'YYYY',
  // used for statement month
  YEAR_MONTH = 'YYYY-MM',
  YEAR_QUARTER = 'YYYY-Q',
}

export enum DATE_FORMATS_LUXON {
  // For date inputs.  Generally should be used where user is entering values
  INPUT = 'LL-dd-y',
  // Standard short display.  If you're displaying a date value to the user it will probably be this one or the long version
  DISPLAY_SHORT = 'LLL dd, y',
  // Shorter version of DISPLAY_SHORT with a short year
  DISPLAY_SHORT_YEAR = 'LLL dd, \u2018yy',
  // Standard long date display
  DISPLAY_LONG = 'LLLL dd, y',
  // Standard long date display with shortened day
  DISPLAY_LONG_SHORT_DAY = 'LLLL d, y',
  // Standard full date/time display.
  DISPLAY_FULL = 'LLL dd yyyy h:mm:ss a',
  // Numbers only display
  DISPLAY_SIMPLE = 'LL/dd/y',
  // Shortest display string for year
  DISPLAY_ABBR = 'L/dd/yy',
  // Used for gusto api calls. Gets and posts need to use this
  GUSTO_SUBMIT = 'yyyy-LL-dd',
  // Used when we need to know month and year only ie monthly reports
  MONTH_YEAR = 'LLL y',
  // Used when you need the full month name
  MONTH_YEAR_LONG = 'LLLL y',
  // Used when year isn't needed
  MONTH_DAY = 'LLL d',
  // Used when year isn't needed with zero-padded day
  MONTH_DAY_PADDED = 'LLL dd',
  // Used when year isn't needed, but you want the full month name
  MONTH_DAY_LONG = 'LLLL d',
  // Used for quarter displays
  QUARTER = 'q, y',
  // Used for time display.  Can be combined with above
  TIME = 'h:mm a',
  // Used where day of the week should be displayed, ie in Payroll reminders
  DAY_OF_WEEK_DAY_MONTH = 'cccc LLLL dd',
  /** Sortable timestamp. Value corrected from 'y-LL-dd' to 'yyyy-MM-dd' — output is identical for all valid dates. Aligns with API enum. */
  TIMESTAMP = 'yyyy-MM-dd HH:mm:ss',
  DISPLAY_TIMEZONE = 'LLL dd y TTT',
  // https://datatracker.ietf.org/doc/html/rfc5545 used by some apis
  RFC5545 = "yLLdd'T'HHmmss'Z'",
  // Used when querying transactions
  YEAR_MONTH_DAY = 'yyyy-MM-dd',
  // Used for bookkeeping report dates
  YEAR_MONTH = 'yyyy-MM',
  MONTH = 'LLLL',
  // Used for API date params in MM-dd-yyyy format (e.g. transaction filters, reconciliation URLs)
  API_INPUT = 'MM-dd-yyyy',
  // Used when displaying full month name with year (e.g. "March 2024")
  MONTH_YEAR_FULL = 'MMMM yyyy',
  // Abbreviated month with 4-digit year (e.g. "Mar 2024") — date-fns/react-datepicker syntax
  MONTH_YEAR_SHORT = 'MMM yyyy',
  // Abbreviated month name only (e.g. "Mar")
  MONTH_ABBR = 'LLL',
  // Zero-padded numeric month (e.g. "03")
  MONTH_ZERO_PADDED = 'MM',
  // Four-digit year only (e.g. "2024")
  YEAR_FOUR_DIGIT = 'yyyy',
  // Time without space before am/pm (e.g. "9:30am")
  TIME_NO_SPACE = 'h:mma',
  // Full datetime with weekday (e.g. "9:30am, Tuesday, March 05, 2024")
  DATETIME_WEEKDAY_LONG = 'h:mma, EEEE, LLLL dd, yyyy',
  // Full datetime with weekday and timezone abbreviation (e.g. "9:30am EDT, Tuesday, March 05, 2024")
  DATETIME_WEEKDAY_LONG_TZ = 'h:mma ZZZZ, EEEE, LLLL dd, yyyy',
  // Weekday with full date (e.g. "Tuesday, March 05, 2024")
  WEEKDAY_DATE_LONG = 'EEEE, LLLL dd, yyyy',
  // Weekday, date, then time with timezone (e.g. "Tuesday, March 05, 2024 at 9:30am EDT")
  WEEKDAY_DATE_LONG_AT_TIME_TZ = "EEEE, LLLL dd, yyyy 'at' h:mma ZZZZ",
  // Weekday, short date, then time with timezone (e.g. "Tuesday, Oct 27 at 1:00 PM EDT")
  WEEKDAY_SHORT_DATE_AT_TIME_TZ = "EEEE, LLL d 'at' h:mm a ZZZZ",
  // Short date with single-digit day, no zero-padding (e.g. "Mar 5, 2024")
  DISPLAY_SHORT_ALT = 'MMM d, yyyy',
  // AM or PM indicator only (e.g. "AM")
  AMPM_ONLY = 'a',
  /** 12-hour zero-padded time, NO am/pm suffix (e.g. "09:30" for 9 AM, "01:05" for 1 PM).
   *  Pair with AMPM_ONLY for unambiguous display. Use TIME_NO_SPACE for compact 12-hr + suffix. */
  TIME_PADDED = 'hh:mm',
  // Datetime input combining INPUT date with 12-hour time + meridian (e.g. "01-15-2024 09:30 AM")
  DATETIME_INPUT = 'LL-dd-y hh:mm a',
  /** fromFormat parsing only — accepts single-digit months ('2024-1') and zero-padded ('2024-01').
   *  Do NOT use with toFormat(). For output/display use YEAR_MONTH ('yyyy-MM'). */
  YEAR_MONTH_LOOSE = 'yyyy-M',
  // ISO-like local datetime without timezone, used for DatePicker min/max
  DATETIME_LOCAL = "yyyy-MM-dd'T'HH:mm:ss",
  // Full formatting month name only (e.g. "January") — formatting variant, same output as MONTH in English
  MONTH_LONG = 'MMMM',
  // Long display with single-digit day and 4-digit year (e.g. "January 1, 2024")
  DISPLAY_LONG_SHORT_DAY_FULL = 'LLLL d, yyyy',
  // Short display with non-padded day (e.g. "Mar 5, 2024") — matches moment's 'MMM D, YYYY'
  DISPLAY_SHORT_NO_PAD = 'LLL d, y',
  // Input format with 2-digit year (e.g. "03-31-24") — for filenames and compact date strings
  INPUT_SHORT_YEAR = 'LL-dd-yy',
  INPUT_SLASH_UNPADDED = 'M/d/yyyy',
  // Abbreviated month + day + year + time with seconds (e.g. "Jan 01 2024, 12:00:00 am")
  ABBR_DAY_YEAR_TIME = 'LLL dd yyyy, hh:mm:ss a',
}

/** Branded alias — use this as the parameter type for any function that accepts a Luxon format string. */
export type LuxonFormat = DATE_FORMATS_LUXON

/**
 * @deprecated Use getLuxonBusinessDay() instead. Will be removed in PR 6 after all consumers are migrated.
 */
export const getMomentBD = () => {
  // Taken from https://www.frbservices.org/about/holiday-schedules.  These are used for payday calculation
  const HOLIDAYS_2026 = [
    '01-01-2026',
    '01-19-2026',
    '02-16-2026',
    '05-25-2026',
    '06-19-2026',
    '07-03-2026',
    '09-07-2026',
    '10-12-2026',
    '11-11-2026',
    '11-26-2026',
    '12-25-2026',
  ]

  const HOLIDAYS_2027 = [
    '01-01-2027',
    '01-18-2027',
    '02-15-2027',
    '05-31-2027',
    '06-18-2027',
    '07-05-2027',
    '09-06-2027',
    '10-11-2027',
    '11-11-2027',
    '11-25-2027',
    '12-24-2027',
  ]

  const HOLIDAYS_2028 = [
    '12-31-2027',
    '01-17-2028',
    '02-21-2028',
    '05-29-2028',
    '06-19-2028',
    '07-04-2028',
    '09-04-2028',
    '10-09-2028',
    '11-10-2028',
    '11-23-2028',
    '12-25-2028',
  ]

  const HOLIDAYS_2029 = [
    '01-01-2029',
    '01-15-2029',
    '02-19-2029',
    '05-28-2029',
    '06-19-2029',
    '07-04-2029',
    '09-03-2029',
    '10-08-2029',
    '11-12-2029',
    '11-22-2029',
    '12-25-2029',
  ]

  MomentBD.updateLocale('us', {
    holidays: [
      ...HOLIDAYS_2026,
      ...HOLIDAYS_2027,
      ...HOLIDAYS_2028,
      ...HOLIDAYS_2029,
    ],
    holidayFormat: DATE_FORMATS.INPUT,
  })

  return MomentBD
}

/**
 * Luxon-based business day calculator with U.S. federal holiday definitions (2025–2035).
 * Replaces moment-business-days dependency for payroll calculations.
 *
 * Unlike moment-business-days' addBusinessDay which mutated the original instance,
 * addBusinessDays returns a new DateTime and leaves the original unchanged.
 *
 * @returns Object with `isBusinessDay(date)` and `addBusinessDays(date, days)` methods.
 *
 * @example
 * const bd = getLuxonBusinessDay()
 * const futureDate = bd.addBusinessDays(DateTime.now(), 5)
 * const isToday = bd.isBusinessDay(DateTime.now())
 */
export const getLuxonBusinessDay = () => {
  // Sourced from https://www.frbservices.org/about/holiday-schedules
  const HOLIDAYS_2025_LX = [
    '05-26-2025',
    '06-19-2025',
    '07-04-2025',
    '09-01-2025',
    '10-13-2025',
    '11-11-2025',
    '11-27-2025',
    '12-25-2025',
  ]

  const HOLIDAYS_2026_LX = [
    '01-01-2026',
    '01-19-2026',
    '02-16-2026',
    '05-25-2026',
    '06-19-2026',
    '07-03-2026',
    '09-07-2026',
    '10-12-2026',
    '11-11-2026',
    '11-26-2026',
    '12-25-2026',
  ]

  const HOLIDAYS_2027_LX = [
    '01-01-2027',
    '01-18-2027',
    '02-15-2027',
    '05-31-2027',
    '06-18-2027',
    '07-05-2027',
    '09-06-2027',
    '10-11-2027',
    '11-11-2027',
    '11-25-2027',
    '12-24-2027',
  ]

  const HOLIDAYS_2028_LX = [
    '12-31-2027',
    '01-17-2028',
    '02-21-2028',
    '05-29-2028',
    '06-19-2028',
    '07-04-2028',
    '09-04-2028',
    '10-09-2028',
    '11-10-2028',
    '11-23-2028',
    '12-25-2028',
  ]

  const HOLIDAYS_2029_LX = [
    '01-01-2029',
    '01-15-2029',
    '02-19-2029',
    '05-28-2029',
    '06-19-2029',
    '07-04-2029',
    '09-03-2029',
    '10-08-2029',
    '11-12-2029',
    '11-22-2029',
    '12-25-2029',
  ]

  const HOLIDAYS_2030_LX = [
    '01-01-2030',
    '01-21-2030',
    '02-18-2030',
    '05-27-2030',
    '06-19-2030',
    '07-04-2030',
    '09-02-2030',
    '10-14-2030',
    '11-11-2030',
    '11-28-2030',
    '12-25-2030',
  ]

  const HOLIDAYS_2031_LX = [
    '01-01-2031',
    '01-20-2031',
    '02-17-2031',
    '05-26-2031',
    '06-19-2031',
    '07-04-2031',
    '09-01-2031',
    '10-13-2031',
    '11-11-2031',
    '11-27-2031',
    '12-25-2031',
  ]

  const HOLIDAYS_2032_LX = [
    '01-01-2032',
    '01-19-2032',
    '02-16-2032',
    '05-31-2032',
    '06-18-2032',
    '07-05-2032',
    '09-06-2032',
    '10-11-2032',
    '11-11-2032',
    '11-25-2032',
    '12-24-2032',
  ]

  const HOLIDAYS_2033_LX = [
    '12-31-2032',
    '01-17-2033',
    '02-21-2033',
    '05-30-2033',
    '06-19-2033',
    '07-04-2033',
    '09-05-2033',
    '10-10-2033',
    '11-11-2033',
    '11-24-2033',
    '12-26-2033',
  ]

  const HOLIDAYS_2034_LX = [
    '01-01-2034',
    '01-16-2034',
    '02-20-2034',
    '05-29-2034',
    '06-19-2034',
    '07-04-2034',
    '09-04-2034',
    '10-09-2034',
    '11-12-2034',
    '11-23-2034',
    '12-25-2034',
  ]

  const HOLIDAYS_2035_LX = [
    '01-01-2035',
    '01-15-2035',
    '02-19-2035',
    '05-28-2035',
    '06-19-2035',
    '07-04-2035',
    '09-03-2035',
    '10-08-2035',
    '11-11-2035',
    '11-22-2035',
    '12-25-2035',
  ]

  const holidays = new Set([
    ...HOLIDAYS_2025_LX,
    ...HOLIDAYS_2026_LX,
    ...HOLIDAYS_2027_LX,
    ...HOLIDAYS_2028_LX,
    ...HOLIDAYS_2029_LX,
    ...HOLIDAYS_2030_LX,
    ...HOLIDAYS_2031_LX,
    ...HOLIDAYS_2032_LX,
    ...HOLIDAYS_2033_LX,
    ...HOLIDAYS_2034_LX,
    ...HOLIDAYS_2035_LX,
  ])

  const isWeekend = (date: DateTime): boolean =>
    date.weekday === 6 || date.weekday === 7

  const isHoliday = (date: DateTime): boolean =>
    holidays.has(date.toFormat(DATE_FORMATS_LUXON.INPUT))

  const isBusinessDay = (date: DateTime): boolean =>
    !isWeekend(date) && !isHoliday(date)

  const addBusinessDays = (date: DateTime, days: number): DateTime => {
    let current = date
    let remaining = days
    while (remaining > 0) {
      current = current.plus({ days: 1 })
      if (isBusinessDay(current)) {
        remaining--
      }
    }
    return current
  }

  return { isBusinessDay, addBusinessDays }
}

// Returns a JS Date whose local-time components equal the UTC wall-clock values of `date`.
// Used for DatePicker min/max props which interpret the Date in local time.
export const utcMomentToUTCDate = (date: string) =>
  new Date(
    DateTime.fromISO(date, { zone: 'utc' }).toFormat(
      DATE_FORMATS_LUXON.DATETIME_LOCAL
    )
  )

/** This is returns a Luxon DateTime */
export const isoToUTCDateTime = (isoDate: string) =>
  DateTime.fromISO(isoDate, { zone: 'utc' })

export const formatISOFromUTC = (
  isoDate: string | undefined | null,
  format: DATE_FORMATS_LUXON = DATE_FORMATS_LUXON.INPUT
) => {
  if (!isoDate) {
    return ''
  }
  const date = isoToUTCDateTime(isoDate)
  if (!date.isValid) {
    return ''
  }
  return date.toFormat(format)
}

/** Parsed in the viewer's own zone, unlike formatISOFromUTC: for a real moment, not a billing anchor. */
export const formatISOLocal = (
  isoDate: string | undefined | null,
  format: DATE_FORMATS_LUXON = DATE_FORMATS_LUXON.INPUT
) => {
  if (!isoDate) {
    return ''
  }
  const date = DateTime.fromISO(isoDate)
  if (!date.isValid) {
    return ''
  }
  return date.toFormat(format)
}

/** The time for a timestamp from today, otherwise its month and day (e.g. "2:40 PM", "Sep 19"). */
export const formatTimeOrMonthDay = (
  iso: string,
  now: DateTime = DateTime.now()
): string => {
  const date = DateTime.fromISO(iso)
  return date.hasSame(now, 'day')
    ? date.toFormat(DATE_FORMATS_LUXON.TIME)
    : date.toFormat(DATE_FORMATS_LUXON.MONTH_DAY)
}

export const formatConversationEventTime = (iso: string): string =>
  DateTime.fromISO(iso).toFormat(
    `${DATE_FORMATS_LUXON.MONTH_DAY}, ${DATE_FORMATS_LUXON.TIME}`
  )

/** A timestamp's day relative to today (e.g. "today", "yesterday", "3 days ago"). */
export const formatRelativeCalendar = (
  iso: string,
  now: DateTime = DateTime.now()
): string => DateTime.fromISO(iso).toRelativeCalendar({ base: now }) ?? ''

export const formatShortDateTimeWithZone = (iso: string): string =>
  DateTime.fromISO(iso).toLocaleString({
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })

export const convertUtcToLocalDate = (isoDate?: string | DateTime | null) => {
  if (!isoDate) {
    return null
  }
  const utcDate =
    typeof isoDate === 'string'
      ? isoToUTCDateTime(isoDate)
      : isoDate.setZone('utc')

  if (!utcDate.isValid) {
    logHeardError('Invalid UTC date', {
      isoDate: typeof isoDate === 'string' ? isoDate : isoDate.toISO(),
      invalidReason: utcDate.invalidReason,
    })
    return null
  }

  return DateTime.fromObject({
    year: utcDate.year,
    month: utcDate.month,
    day: utcDate.day,
  })
}

export const getTimeDifferenceInDaysAndHours = (time: string) => {
  const parsedTime = DateTime.fromISO(time)
  if (!parsedTime.isValid) {
    return ''
  }
  const differenceInHours = Math.round(
    (new Date().getTime() - parsedTime.toJSDate().getTime()) / (1000 * 3600)
  )
  if (differenceInHours < 23) {
    return `${differenceInHours} hour(s)`
  } else {
    const days = Math.floor(differenceInHours / 24)
    const hours = differenceInHours % 24
    return `${days} day(s) ${hours} hour(s)`
  }
}

const MONTH_NAMES =
  'January|February|March|April|May|June|July|August|September|October|November|December'

// Matches a date as written in prose: March 15, June 30th, January 1, 2027.
// The year is only part of the match when the text already carries one.
// Returns a new regex each call because the global flag makes `lastIndex`
// stateful, which a shared instance would leak between callers.
export const proseDatePattern = (): RegExp =>
  new RegExp(
    `\\b(?:${MONTH_NAMES})\\s+\\d{1,2}(?:st|nd|rd|th)?(?:,\\s*\\d{4})?\\b`,
    'g'
  )

export const getOrdinal = (n: number) => {
  const end = ['th', 'st', 'nd', 'rd']
  const endKey = n % 100
  const endString = end[(endKey - 20) % 10] || end[endKey] || end[0]
  return `${n}${endString}`
}

// Formats the date with ordinal after the day part
// so formatDateWithOrdinal(DateTime.fromObject({ day: 1, month: 1, year: 2020 }), DATE_FORMATS_LUXON.MONTH_DAY) will output Jan 1st
export const formatDateWithOrdinal = (
  date: DateTime,
  format: DATE_FORMATS_LUXON
) => date.toFormat(format.replace(/d+/, `'${getOrdinal(date.get('day'))}'`))

/**
 * Turns a filter date (`MM-dd-yyyy`) into the ISO bound of that day in UTC. Use it for
 * calendar-date columns such as `transactions.date`, which are stored at midnight UTC.
 */
export const filterDateToIso = (
  value: string | null | undefined,
  edge: 'start' | 'end'
) => {
  if (!value) return undefined
  const parsed = DateTime.fromFormat(value, DATE_FORMATS_LUXON.INPUT, {
    zone: 'utc',
  })
  if (!parsed.isValid) {
    logHeardError('filterDateToIso: unparseable date filter', {
      dateStr: value,
    })
    return undefined
  }
  const bound = edge === 'start' ? parsed.startOf('day') : parsed.endOf('day')
  return bound.toISO() ?? undefined
}

/**
 * Converts a YYYY-MM string to a DateTime object.
 * '2024-1' and '2024-01' are acceptable.
 */
export const convertYearMonthStringToDateTime = (yearMonth?: string | null) => {
  if (!yearMonth) {
    return null
  }

  const year = yearMonth.split('-')[0]
  const month = yearMonth.split('-')[1]

  if (!year || !month) {
    return null
  }

  const dateObject = DateTime.fromObject({
    year: Number(year),
    month: Number(month),
  })
  if (!dateObject.isValid) {
    return null
  }
  return dateObject
}

export function formatUTCDateString({
  value,
  format = DATE_FORMATS_LUXON.INPUT,
}: {
  value: DateTime | string
  format?: string
}) {
  if (typeof value === 'string' || value instanceof String) {
    value = DateTime.fromISO(value as string, { zone: 'utc' })
  }

  if (!value.isValid) {
    return ''
  }

  return value.toFormat(format)
}

export function toUTCDateString({
  value,
  format = DATE_FORMATS_LUXON.INPUT,
}: {
  value: Date | string
  format?: string
}) {
  return formatUTCDateString({
    value: DateTime.fromJSDate(
      value instanceof Date ? value : new Date(value),
      { zone: 'utc' }
    ),
    format,
  })
}

export function yesterdayUTCDateString({
  format = DATE_FORMATS_LUXON.INPUT,
}: { format?: string } = {}) {
  return formatUTCDateString({
    value: DateTime.utc().minus({ days: 1 }),
    format,
  })
}

export function tomorrowUTCDateString({
  value,
  format = DATE_FORMATS_LUXON.INPUT,
}: {
  value: string
  format?: string
}) {
  return formatUTCDateString({
    value: DateTime.fromISO(value, { zone: 'utc' }).plus({ days: 1 }),
    format,
  })
}

/** "January through August 2026", or "November 2025 through February 2026" across years. */
export const formatMonthRange = (
  firstYearMonth: string,
  lastYearMonth: string
) => {
  const first = DateTime.fromFormat(
    firstYearMonth,
    DATE_FORMATS_LUXON.YEAR_MONTH
  )
  const last = DateTime.fromFormat(lastYearMonth, DATE_FORMATS_LUXON.YEAR_MONTH)
  const lastLabel = last.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)
  if (first.hasSame(last, 'month')) return lastLabel
  const firstLabel = first.toFormat(
    first.hasSame(last, 'year')
      ? DATE_FORMATS_LUXON.MONTH_LONG
      : DATE_FORMATS_LUXON.MONTH_YEAR_LONG
  )
  return `${firstLabel} through ${lastLabel}`
}
